import { Outlet, createBrowserRouter } from 'react-router';

import { BusinessPagesLayout } from '~app/shell';

import {
  AnonymousGuard,
  AuthGate,
  PermissionsGuard,
  RequireAuth,
  RootRedirect,
  LoginPage,
  AccessDeniedPage,
} from '~domains/auth';
import { AppChatWidget } from '~domains/chat';
import { DashboardPage } from '~domains/dashboard';
import { CreateOrderPage, OrdersPage } from '~domains/orders';
import {
  PartnerApplicationCreatePage,
  PartnerApplicationEditPage,
  PartnerApplicationMenuPage,
  PartnerApplicationSignPage,
} from '~domains/partner-application';
import { EmployeeProfilePage, ProfilePage } from '~domains/profile';
import {
  LoansPage,
  PaymentsPage,
  TransactionsPage,
} from '~domains/transactions';

import * as Sentry from '@breeze/web-lib-sentry';

import { PartnerRedirect } from './partner-redirect';
import { ProfileStateGuard } from './profile-state-guard';

const createBrowserRouterWithSentry =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

const routes = [
  {
    element: <AuthGate />,
    children: [
      {
        path: '*',
        element: <RootRedirect />,
      },
      {
        path: 'login',
        element: (
          <AnonymousGuard>
            <LoginPage />
          </AnonymousGuard>
        ),
      },
      {
        element: (
          <RequireAuth>
            <Outlet />
            <AppChatWidget />
          </RequireAuth>
        ),
        children: [
          {
            path: 'access-denied',
            element: (
              <PermissionsGuard permissions="none-of-pages">
                <AccessDeniedPage />
              </PermissionsGuard>
            ),
          },
          {
            path: 'partner',
            element: (
              <PermissionsGuard permissions="none-of">
                <Outlet />
              </PermissionsGuard>
            ),
            children: [
              {
                index: true,
                element: <PartnerRedirect />,
              },
              {
                path: 'profile',
                element: (
                  <ProfileStateGuard shouldBeFilled={false}>
                    <ProfilePage />
                  </ProfileStateGuard>
                ),
              },
              {
                element: (
                  <ProfileStateGuard shouldBeFilled={true}>
                    <Outlet />
                  </ProfileStateGuard>
                ),
                children: [
                  {
                    path: 'application',
                    element: <PartnerApplicationCreatePage />,
                  },
                  {
                    path: 'application/menu',
                    element: <PartnerApplicationMenuPage />,
                  },
                  {
                    path: 'application/:id',
                    element: <PartnerApplicationEditPage />,
                  },
                  {
                    path: 'application/:id/sign',
                    element: <PartnerApplicationSignPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'profile',
            element: (
              <PermissionsGuard permissions={['PROFILE_VIEW']}>
                <EmployeeProfilePage />
              </PermissionsGuard>
            ),
          },
          {
            element: <BusinessPagesLayout />,
            children: [
              {
                path: 'dashboard',
                element: (
                  <PermissionsGuard permissions={['DASHBOARD_VIEW']}>
                    <DashboardPage />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'transactions',
                element: <TransactionsPage />,
                children: [
                  {
                    path: 'loans',
                    element: (
                      <PermissionsGuard permissions={['APPLICATIONS_VIEW']}>
                        <LoansPage />
                      </PermissionsGuard>
                    ),
                  },
                  {
                    path: 'payments',
                    element: (
                      <PermissionsGuard permissions={['TRANSACTIONS_VIEW']}>
                        <PaymentsPage />
                      </PermissionsGuard>
                    ),
                  },
                ],
              },
              {
                path: 'orders',
                element: (
                  <PermissionsGuard permissions={['ORDERS_VIEW']}>
                    <OrdersPage />
                  </PermissionsGuard>
                ),
              },
            ],
          },
          {
            path: 'orders/create',
            element: (
              <PermissionsGuard permissions={['ORDERS_CREATE']}>
                <CreateOrderPage />
              </PermissionsGuard>
            ),
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouterWithSentry(routes, { basename });
